/**
 * AllysListingMapCard - Sidebar listing card styled to match the Allys mailer aesthetic
 *
 * Clean, editorial layout inspired by the mailer district sidebar:
 *  - Teal (#2D5A6B) primary with gold (#D4A017) accents
 *  - Subtle borders (#e8ebef), light fills (#f0f5f7)
 *  - Small indicator dots for active/hover states
 *  - Plus Jakarta Sans typography
 */

'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Bed, Bath, Maximize, MapPin } from 'lucide-react';
import { FormatPrice } from '@/components/ui/FormatPrice';
import type { ListingCardData } from '@/components/listings/types';
import { getListingTypeLabel } from '@/components/listings/types';

function getAllysListingTypeColor(type: string): string {
  if (type === 'SALE' || type === 'COMMERCIAL_SALE') {
    return 'bg-[#2D5A6B] text-white';
  }
  if (type.includes('RENTAL') || type === 'ROOM_RENTAL' || type === 'SUBLET') {
    return 'bg-[#D4A017] text-white';
  }
  if (type === 'AUCTION') {
    return 'bg-gradient-to-r from-[#D4A017] to-[#E8C84A] text-[#1a3540]';
  }
  return 'bg-gray-500 text-white';
}

export interface AllysListingMapCardProps {
  listing: ListingCardData;
  href: string;
  isSelected?: boolean;
  isHovered?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
  className?: string;
}

export function AllysListingMapCard({
  listing,
  href,
  isSelected = false,
  isHovered = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  className = '',
}: AllysListingMapCardProps) {
  const { property, location, listingType, price } = listing;

  const addressLine = location?.addressLine1 || '';
  const areaString = [
    location?.suburb,
    location?.city,
    location?.district,
  ].filter(Boolean).join(', ') || location?.name || '';

  const isHighlighted = isSelected || isHovered;

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      className={`
        group cursor-pointer transition-all duration-200 rounded-xl overflow-hidden
        ${isHighlighted
          ? 'bg-[#f0f5f7] ring-1 ring-[#2D5A6B]/20 shadow-sm'
          : 'bg-white hover:bg-[#f8f9fa] border border-[#e8ebef]'
        }
        ${className}
      `}
      style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
    >
      <Link href={href} className="flex no-underline text-inherit">
        {/* Image */}
        <div className="relative w-[108px] sm:w-[128px] flex-shrink-0 bg-[#f0f5f7]">
          {property.primaryImageUrl ? (
            <Image
              src={property.primaryImageUrl}
              alt={property.name}
              fill
              className="object-cover"
              loading="lazy"
              sizes="128px"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#c8d3d8] text-xs font-medium">
              No Image
            </div>
          )}
          {/* Type badge */}
          <div className={`absolute top-1.5 left-1.5 px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase rounded-full ${getAllysListingTypeColor(listingType)}`}>
            {getListingTypeLabel(listingType)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col gap-0.5 min-w-0">
          {/* Indicator + Title row */}
          <div className="flex items-start gap-2">
            <span
              className={`mt-1.5 w-[7px] h-[7px] rounded-full flex-shrink-0 transition-colors duration-200 ${
                isHighlighted ? 'bg-[#87CEEB]' : 'bg-[#c8d3d8] group-hover:bg-[#4a8a9e]'
              }`}
            />
            <h4 className="text-[13px] font-semibold text-[#1a3540] line-clamp-1 leading-tight">
              {listing.mainHeadline || property.name}
            </h4>
          </div>

          {/* Location */}
          {(addressLine || areaString) && (
            <div className="pl-[15px]">
              {addressLine && (
                <p className="flex items-center gap-1 text-[11px] text-[#1a3540] line-clamp-1">
                  <MapPin size={9} className="text-[#D4A017] flex-shrink-0" />
                  {addressLine}
                </p>
              )}
              {areaString && (
                <p className={`text-[10px] text-[#6b7280] line-clamp-1 ${addressLine ? 'pl-[13px]' : 'flex items-center gap-1'}`}>
                  {!addressLine && <MapPin size={9} className="text-[#D4A017] flex-shrink-0" />}
                  {areaString}
                </p>
              )}
            </div>
          )}

          {/* Price */}
          <div className="text-[13px] font-bold text-[#2D5A6B] pl-[15px]">
            {listing.displayPriceText ? (
              <span className="text-xs">{listing.displayPriceText}</span>
            ) : price ? (
              <FormatPrice
                amount={price.amount}
                currency={price.currency}
                frequency={price.frequency ?? null}
                size="sm"
                priceOnRequestText="Price on request"
              />
            ) : (
              <span className="text-xs text-[#c8d3d8]">Price on request</span>
            )}
          </div>

          {/* Features */}
          <div className="flex items-center gap-3 text-[11px] text-[#6b7280] mt-auto pl-[15px]">
            {property.bedrooms !== null && property.bedrooms > 0 && (
              <span className="flex items-center gap-1">
                <Bed size={11} className="text-[#2D5A6B]" />
                <span className="font-semibold">{property.bedrooms}</span>
              </span>
            )}
            {property.bathrooms !== null && property.bathrooms > 0 && (
              <span className="flex items-center gap-1">
                <Bath size={11} className="text-[#4a8a9e]" />
                <span className="font-semibold">{property.bathrooms}</span>
              </span>
            )}
            {property.totalFloorArea !== null && property.totalFloorArea > 0 && (
              <span className="flex items-center gap-1">
                <Maximize size={11} className="text-[#c8d3d8]" />
                <span>{property.totalFloorArea.toLocaleString()}</span>
                <span className="text-[9px]">{property.totalFloorAreaUnit?.toLowerCase() || 'sqm'}</span>
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

export default AllysListingMapCard;
